/**
 * 外車接單歷史（使用者 2026-09-18）：簡單版——以接單日期篩選，預設今天，前後一天切換。
 * 只列本人接過的單（不分狀態）；比會員的歷史紀錄查詢簡單得多，刻意不做區間與統計。
 */
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Paper, Typography } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { zhTW } from 'date-fns/locale'
import { format, parseISO } from 'date-fns'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { ExtShell, EXT_MAIN } from './ExtGuard'
import { getExtHistory } from './extService'
import { ExtNotBoundError, extErrorMessage } from './extApiClient'
import { EMPTY_LOADED_LABEL, MISSION_STATUS_LABEL, type ExtMission } from './types'

const todayStr = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
const shiftDate = (s: string, days: number) => {
  const d = new Date(`${s}T00:00:00`)
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
const fmtTime = (iso?: string | null) => (iso ? new Date(iso).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' }) : '')

const STATUS_COLOR: Record<string, string> = { '13': '#1976d2', '20': '#ed6c02', '30': '#2e7d32' }

const ExtHistoryPage: React.FC = () => {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const back = params.get('back') || '/ext/me'
  const [date, setDate] = useState(todayStr())
  const [list, setList] = useState<ExtMission[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (d: string) => {
    setLoading(true)
    setError(null)
    try {
      setList(await getExtHistory(d))
    } catch (err) {
      if (err instanceof ExtNotBoundError) navigate(`/ext/bind?back=${encodeURIComponent(`/ext/history`)}`, { replace: true })
      else setError(extErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [navigate])

  useEffect(() => { load(date) }, [date, load])

  const isToday = date === todayStr()

  return (
    <ExtShell>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(back)} sx={{ color: EXT_MAIN }}>返回</Button>
        <Typography variant="h6" sx={{ fontWeight: 700, color: EXT_MAIN }}>接單紀錄</Typography>
        <Box sx={{ width: 72 }} />
      </Box>

      {/* 日期篩選：前一天 / 日期 / 後一天（不可超過今天） */}
      <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton onClick={() => setDate((d) => shiftDate(d, -1))} sx={{ color: EXT_MAIN }}><ChevronLeftIcon /></IconButton>
        {/* 與司機日報同一種手機日期選擇器（全螢幕、年月日輪），不用原生 input[type=date] */}
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={zhTW}>
          <MobileDatePicker
            value={parseISO(date)}
            maxDate={new Date()}
            format="yyyy/MM/dd"
            views={['year', 'month', 'day']}
            // 2026-10 客戶反映「選了日期沒作用」：原本用 onAccept，手機版要再按「確定」才生效，點日期後直接關掉就不會查。
            // 改用 onChange（與會員 DailyReportPage 相同），點到日期即查詢；日期無效（輸入中）不動作。
            onChange={(v) => { if (v && !Number.isNaN(v.getTime())) setDate(format(v, 'yyyy-MM-dd')) }}
            closeOnSelect
            slotProps={{
              textField: { fullWidth: true, size: 'small', inputProps: { readOnly: true, style: { textAlign: 'center', fontWeight: 600 } } },
              toolbar: { toolbarFormat: 'yyyy/MM/dd' },
            }}
          />
        </LocalizationProvider>
        <IconButton disabled={isToday} onClick={() => setDate((d) => shiftDate(d, 1))} sx={{ color: EXT_MAIN }}><ChevronRightIcon /></IconButton>
        {!isToday && <Button size="small" onClick={() => setDate(todayStr())} sx={{ color: EXT_MAIN, flexShrink: 0 }}>今天</Button>}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: EXT_MAIN }} /></Box>
      ) : list.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>{isToday ? '今天' : date} 沒有接單</Typography>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>共 {list.length} 筆</Typography>
          {list.map((m) => (
            <Paper key={m.missionId} elevation={1} sx={{ p: 1.5, mb: 1, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: EXT_MAIN }}>{m.vesselCode} {m.vesselName ?? ''}</Typography>
                <Chip size="small" label={MISSION_STATUS_LABEL[m.status] ?? m.status}
                  sx={{ backgroundColor: STATUS_COLOR[m.status] ?? '#9e9e9e', color: 'white', fontWeight: 600, height: 22 }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {m.originShortName || m.originLocation || '-'} → {m.destShortName || m.destinationLocation || '-'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {m.containerSize ? `${m.containerSize} 呎` : ''} {EMPTY_LOADED_LABEL[m.emptyLoaded ?? ''] ?? ''}
                {m.containerNo ? ` · ${m.containerNo}` : ''}
                {` · 接單 ${fmtTime(m.acceptedTime)}`}{m.finishTime ? ` · 完成 ${fmtTime(m.finishTime)}` : ''}
              </Typography>
            </Paper>
          ))}
        </>
      )}
    </ExtShell>
  )
}

export default ExtHistoryPage
